/********************************
 * 지도 관련 자바스크립트
 ********************************
 * @since 	: 2020.09.15
 * @author 	: (주)라임에스
 *
 * @override: 2024-04-24
 * @base 	: ol.js v8.1.0
 */

 import * as React from 'react';
 import 'ol/ol.css';
 import { Map as OlMap, View as OlView, Overlay as OlOverlay, Feature as OlFeature } from 'ol';
 import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
 import { Vector as VectorSource, XYZ as XYZSource, Cluster as ClusterSource} from 'ol/source';
 import { Point as PointGeom, LineString as LineStringGeom, Polygon as PolygonGeom } from 'ol/geom';
 import { Circle as CircleStyle, Fill as FillStyle, Icon as IconStyle, 
    Stroke as StrokeStyle, Style as OlStyle, Text as TextStyle } from 'ol/style';
import { fromLonLat, toLonLat } from "ol/proj";
import { defaults as defaultControls } from 'ol/control';

const selOff = 12;
export class FeatureType { // Struct
    static Point = 1; // ol.geom.Point
    static LineString = 2; // ol.geom.LineString
    static Polygon = 3; // ol.geom.Polygon
    static MultiPoint = 4;
    static MultiLineString = 5;
    static MultiPolygon = 6;
    static Circle = 7;
    static LinearRing = 8;
    static GeometryCollection = 9;
}
export class Proj4jsDef {
    /*
    https://epsg.io/5181
    site 가서
    Proj4js 선택하면
    Definition: JavaScript (Proj4js) 정보 나옵니다.
    예)
    proj4.defs("EPSG:5181","+proj=tmerc +lat_0=38 +lon_0=127 +k=1 +x_0=200000 +y_0=500000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs");
    */
    static Proj4jsDef() {
        console.log("This is a static method.");
    }
    static get(proj) {
        switch (proj) {
            //  비월드 구글맵 OSM Mapbox
            case "EPSG:3857":
                return "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs";
                break;
                //  카카오맵
            case "EPSG:5181":
                return "+proj=tmerc +lat_0=38 +lon_0=127 +k=1 +x_0=200000 +y_0=500000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs";
                break;
                //  네이버
            case "EPSG:5179":
                return "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs";
                break;
            default:
                return "";
                break;
        }
    }
}
export class LsOpenLayers {

    node = null;
    map = null;

    srcProj = null;
    dstProj = null;

    dataList = null; // OLData array
    layerList = null; // Vector Layer array

    isDrawing = false;
    drawingSource = null; // OLData
    mouse = null; // OLMouse
    pixel = null;

    funcMapMouseUp = null; // function pointer event
    funcMapMouseDown = null; // function pointer event
    funcMapMouseMove = null; // function pointer event
    funcMapMouseLeave = null; // function pointer event

    constructor(node, renderCompleteCallback) {
        if (node.nodeType != Node.ELEMENT_NODE || node.nodeName != "DIV") {
            return;
        }
        if (!node.id) {
            return;
        }

        this.node = node;
        this.dataList = new Array();
        this.layerList = new Array();
        // empty map
        this.map = new OlMap({
            view: new OlView({
                center: [0, 0],
            }),
            layers: [],
            target: this.node.id,
            controls: defaultControls({ zoom: false }),
        });
        this.map.getViewport().style.cursor = "grab";

        let t = this;

        // map events
        this.map.on("pointerdown", this.handlePointerDown);
        this.map.on("pointermove", this.handlePointerMove);
        this.map.on("pointerup", this.handlePointerUp);
        this.map.on("wheel", this.handleWheel);
        this.map.getViewport().addEventListener("pointerleave", this.handlePointerLeave);
        this.map.getTargetElement().addEventListener("contextmenu", function(e) {
            e.preventDefault();
        });
        this.map.on("movestart", this.handleMoveStart);
        this.map.on("moveend", this.handleMoveEnd);
        this.map.on("click", this.handleClick);

        this.mouse = new OLMouse();

        this.map.once('rendercomplete', function() {
            t.addVectorLayer("draw-layer");
            t.addVectorLayer("temp-layer");
            let element = document.getElementById("popup");
            if (element) {
                t.addOverlay(new OLOverlay("popup", element));
            }
            if (renderCompleteCallback) {
                renderCompleteCallback(t);
            }
        });
    }
    // Tile 지도 레이어
    addTileLayer(lid, url) {
        let layer = new TileLayer({
            title: lid,
            type: "base",
            className: "base",
            source: new XYZSource({
                url: url,
            }),
        });
        this.map.addLayer(layer);
    };
    // 좌표계 설정
    setProj(s, d) {
        this.srcProj = s;
        this.dstProj = d;
    };
    // 지도 뷰 설정
    setView(xy, zoom, minZ, maxZ) {
        const view = new OlView({
            center: this.transform(xy),
            zoom: zoom,
            minZoom: minZ,
            maxZoom: maxZ,
            //projection: this.dstProj,
        });
        this.map.setView(view);
    }
    // 좌표계 변환 if(inverse=true): 역으로 변환
    transform(xy, inverse = false) {
        if (inverse) {
            return toLonLat(xy);
        }
        return fromLonLat(xy);
    };
    // 벡터 데이터용 레이어 추가
    addVectorLayer(idx, lid) {
        let layer = new VectorLayer({
            title: lid,
            source: new VectorSource({
                features: [],
            }),
            visible: true,
            layerIndex: idx,
            userLayer: true,
        });
        this.map.addLayer(layer);
        this.layerList.push(lid);
    };
    // Cluster 데이터용 레이어 추가
    addClusterLayer(lid) {
        let clusterStyle = function(feature) {
            let size = feature.get('features').length;
            if (size > 1) {
                // Clustered point (multiple features)
                return [
                    new OlStyle({ // outer circle
                        image: new CircleStyle({
                            radius: 30,
                            fill: new FillStyle({
                                color: 'rgba(255, 153, 102, 0.3)'
                            }),
                        }),
                    }),
                    new OlStyle({
                        image: new CircleStyle({ // inner circle
                            radius: 20,
                            fill: new FillStyle({
                                color: 'rgba(255, 165, 0, 0.7)'
                            }),
                        }),
                        text: new TextStyle({
                            font: '18px Noto Sans KR, sans-serif',
                            text: size.toString(),
                            fill: new FillStyle({
                                color: '#fff'
                            }),
                            stroke: new StrokeStyle({
                                color: 'rgba(0, 0, 0, 0.6)',
                                width: 3
                            }),
                        })
                    }),
                ]
            } else {
                // Single feature (unclustered)
                let originalFeature = feature.get('features')[0];
                return originalFeature.getStyle();
            }
        };
        ///
        let layer = new VectorLayer({
            title: lid,
            source: new ClusterSource({
                distance: 60,
                minDistance: 40,
                source: new VectorSource({
                    features: [],
                }),
            }),
            visible: true,
            style: clusterStyle,
        });
        this.map.addLayer(layer);
        this.layerList.push(lid);
    };
    // 레이명으로 레이어를 검색하고 가져오기
    getLayer(lid) {
        return this.map.getLayers().getArray().find(x => x.values_.title === lid);
    };
    // overlay 추가
    addOverlay(olOverlay) {
        const map = this.map;
        const popup = new OlOverlay({
            id: olOverlay.id,
            element: olOverlay.element, // jsp file element 의 보무 element
            offset: olOverlay.offset,
            position: olOverlay.position,
            positioning: olOverlay.positioning,
            stopEvent: olOverlay.stopEvent,
            insertFirst: olOverlay.insertFirst,
            autoPan: olOverlay.autoPan,
            className: olOverlay.className
        });
        map.addOverlay(popup);
        // jsp file element
        olOverlay.element.style.display = "block";
    }
    removeOverlay(olOverlay) {
        //getOverlayById
    }
    removeOverlays() {
        const map = this.map;
        let overlays = map.getOverlays();
        overlays.forEach(function(overlay) {
            map.removeOverlay(overlay);
        });
    }

    unSelectAll() {
        let map = this.map;
        for (let i = this.dataList.length - 1; i >= 0; i--) {
            let olData = this.dataList[i];
            // 지도에서 선택해제
            if (olData.uniId && olData.mouse.selected) {
                let lyr = this.getLayer(olData.lyrId);
                let src = lyr.getSource();
                if (src instanceof ClusterSource) {
                    src = src.getSource();
                } else if (src instanceof VectorSource) {}
                let ftr = src.getFeatureById(olData.uniId);
                if (ftr) {
                    ftr.setStyle(olData.getStyle());
                }
            }
            olData.mouse.default();
        }
        let overlay = map.getOverlayById("popup");
        if (overlay) {
            overlay.element.style.display = "none";
        }
        ///
    }
    selectAll() {
        for (let i = this.dataList.length - 1; i >= 0; i--) {
            let olData = this.dataList[i];
            olData.mouse.selected = true;
        }
    }
    select(pixel) { // select for cluster
        this.unSelectAll();
        let ins = this;
        let map = this.map;
        let coordinate = map.getCoordinateFromPixel(pixel);
        map.forEachFeatureAtPixel(pixel, function(ftr, lyr) {
            let src = lyr.getSource();
            if (src instanceof ClusterSource) {
                ftr = ftr.get('features')[0];
            } else if (src instanceof VectorSource) {}
            ///
            let uniId = ftr.getId();
            let olData = ins.findData(uniId);
            if (olData) {
                olData.mouse.selected = true;
                olData.mouse.moving = true; // select then moving
                ftr.setStyle(olData.getSelectedStyle());
                /*console.log(ftr.getProperties())*/                
                let overlay = map.getOverlayById("popup");
                if (overlay) {
                    const x = olData.userData?.get("x");
                    const y = olData.userData?.get("y");                    
                    if(x && y) { overlay.setPosition(ins.transform([x, y])); }
                    else { overlay.setPosition(coordinate); }                    
                    overlay.element.style.display = "block";
                    const popupHtml = olData.userData?.get("popupHtml");
                    if(popupHtml) {
                        overlay.element.innerHtml = popupHtml;
                    }
                }
                ///
                ins.openPopup(olData);
            }
            return true;
        });
    }
    select2(pixel) { // select for vector
        this.unSelectAll();
        let map = this.map;
        let features = map.getFeaturesAtPixel(pixel);
        let coordinate = map.getCoordinateFromPixel(pixel);
        if (features[0]) {
            let ftr = features[0];
            let uniId = ftr.getId();
            let olData = this.findData(uniId);
            if (olData) {
                olData.mouse.selected = true;
                olData.mouse.moving = true; // select then moving
                ftr.setStyle(olData.getSelectedStyle());
                /*console.log(ftr.getProperties())*/
                let overlay = map.getOverlayById("popup");
                if (overlay) {
                    overlay.setPosition(coordinate);
                    overlay.element.style.display = "block";
                }
            }
        }
    }
    isSelected() {
        let selected = false;
        const filtered = this.dataList.filter((roiData) => roiData.mouse.selected === true);
        if (filtered.length > 0) { // is there any selected data
            selected = true;
        }
        return selected;
    }
    findData(uniId) {
        return this.dataList.find(x => x.uniId && x.uniId === uniId);
    }

    openPopup(olData) {
        if(this.funcMapOpenPopup) {
            this.funcMapOpenPopup(olData);
        }
    }

    handlePointerDown = (event) => {
        /*console.log(event.type);*/

        if (this.funcMapMouseDown) {
            this.funcMapMouseDown(event);
        }

        const map = this.map;
        const x = event.coordinate[0];
        const y = event.coordinate[1];
        const xy = this.transform([x, y], true);
        const lng = xy[0];
        const lat = xy[1];
        this.pixel = [x, y];
        if (this.isDrawing) {
            map.getViewport().style.cursor = "crosshair";
        } else {
            map.getViewport().style.cursor = "grabbing";
        }
    }
    handlePointerMove = (event) => {
        /*console.log(event.type);*/
        /*https://stackoverflow.com/questions/26022029/how-to-change-the-cursor-on-hover-in-openlayers-3*/
        const map = this.map;
        const x = event.coordinate[0];
        const y = event.coordinate[1];
        const xy = this.transform([x, y], true);
        const lng = xy[0];
        const lat = xy[1];
        if (this.isDrawing) {
            map.getViewport().style.cursor = "crosshair";
            if (this.drawingData.length > 0) {
                // drawing data
                let spj = this.srcProj;
                let dpj = this.dstProj;
                let lyr = this.getLayer("temp-layer");
                let src = lyr.getSource();
                src.clear();
                this.drawingData.draw(src, spj, dpj);
                // draw temp line
                let point = this.drawingData.getPoint(this.drawingData.length - 1); // last point
                let olData = new OLPolyline();
                olData.addPoint(point);
                olData.addPoint(new LsPoint(lng, lat));
                olData.draw(src, spj, dpj);
            }
        } else {
            map.getViewport().style.cursor = map.hasFeatureAtPixel(event.pixel) ? 'pointer' : 'grab';
        }

        if (this.funcMapMouseMove) {
            this.funcMapMouseMove(event);
        }
    }
    handlePointerUp = (event) => {
        /*console.log(event.type);*/
        const map = this.map;
        const x = event.coordinate[0];
        const y = event.coordinate[1];
        const xy = this.transform([x, y], true);
        const lng = xy[0];
        const lat = xy[1];
        if (this.isDrawing) {
            if (event.originalEvent.button === 0) { // left click
                map.getViewport().style.cursor = "grab";
                this.drawingData.addPoint(new LsPoint(lng, lat));
                if (this.drawingData.ftrTp === FeatureType.Point) { // click only once
                    this.isDrawing = false;
                    this.drawingData.lyrId = "draw-layer";
                    this.addData(this.drawingData.clone());
                    this.drawingData = null;
                    this.draw();
                } else {
                    let spj = this.srcProj;
                    let dpj = this.dstProj;
                    let lyr = this.getLayer("temp-layer");
                    let src = lyr.getSource();
                    src.clear();
                    this.drawingData.draw(src, spj, dpj);
                }
            } else if (event.originalEvent.button === 2) { // right  click
                map.getViewport().style.cursor = "grab";
                this.isDrawing = false;
                this.drawingData.lyrId = "draw-layer";
                if (this.drawingData.ftrTp === FeatureType.Point) {
                    if (this.drawingData.length == 1) {
                        this.addData(this.drawingData.clone());
                    }
                } else if (this.drawingData.ftrTp === FeatureType.LineString) {
                    if (this.drawingData.length > 1) {
                        this.addData(this.drawingData.clone());
                    }
                } else if (this.drawingData.ftrTp === FeatureType.Polygon) {
                    if (this.drawingData.length > 2) {
                        this.addData(this.drawingData.clone());
                    }
                }
                this.drawingData = null;
                this.draw();
            }
        } else {
            map.getViewport().style.cursor = "grab";
            const distance = distanceToPoint(x, y, this.pixel[0], this.pixel[1]);
            if (distance < 2) {
                this.select(event.pixel);
            }
        }

        if (this.funcMapMouseUp) {
            this.funcMapMouseUp(event);
        }
    }
    handlePointerLeave = (event) => {
        /*console.log(event.type);*/

        if (this.funcMapMouseLeave) {
            this.funcMapMouseLeave(event);
        }
    }
    handleWheel = (event) => {
        /*console.log(event.type);*/
        this.unSelectAll();
        if (this.funcMapMouseWheel) {
            this.funcMapMouseWheel(event);
        }
    }
    handleMoveStart = (event) => {
        this.unSelectAll();
    }
    handleMoveEnd = (event) => {
        const map = this.map;
        /*this.unSelectAll();*/
    }
    handleClick = (event) => {}

    ///
    addData(olData) {
        olData.setUniId(1 + this.dataList.length);
        this.dataList.push(olData);
    }
    draw() {
        let olData, lyr, src, spj, dpj;
        spj = this.srcProj;
        dpj = this.dstProj;
        for (let i = 0; i < this.layerList.length; i++) {
            lyr = this.getLayer(this.layerList[i]);
            src = lyr.getSource();
            if (src instanceof ClusterSource) {
                src = new ClusterSource({
                    distance: 40,
                    minDistance: 40,
                    source: new VectorSource({
                        features: [],
                    })
                });
                lyr.setSource(src);
            } else if (src instanceof VectorSource) {
                src = new VectorSource({
                    features: [],
                });
                lyr.setSource(src);
            }
        }
        for (let i = 0; i < this.dataList.length; i++) {
            olData = this.dataList[i];
            lyr = this.getLayer(olData.lyrId);
            src = lyr.getSource();
            if (src instanceof ClusterSource) {
                src = src.getSource();
                olData.draw(src, spj, dpj);
            } else if (src instanceof VectorSource) {
                olData.draw(src, spj, dpj);
            }
        }
    }

    setVisible(lyrId, visible) {
        let layer = this.getLayer(lyrId);
        if (layer) {
            layer.setVisible(visible);
        }
    }
    flyTo(xy, done /*callback function*/ ) {
        const view = this.map.getView();
        const duration = 2000;
        const zoom = view.getZoom();
        let parts = 2;
        let called = false;

        function callback(complete) {
            --parts;
            if (called) {
                return;
            }
            if (parts === 0 || !complete) {
                called = true;
                if (done) {
                    done(complete);
                }
            }
        }
        view.animate({
                center: this.transform(xy),
                duration: duration,
            },
            callback,
        );
        view.animate({
                zoom: zoom - 0.2,
                duration: duration / 2,
            }, {
                zoom: zoom,
                duration: duration / 2,
            },
            callback,
        );
    }
    panTo(xy) {
        const view = this.map.getView();
        const duration = 2000;
        view.animate({
            center: this.transform(xy),
            duration: duration,
        }, );
    }
    getZoom() {
        return this.map.getView().getZoom();
    }

    setTarget(v) {
        const map = this.map;
        map.setTarget(v);
    }
}
//////////////////////////////////////////////////////////////////
export class LsPoint { // LimeS Point
    x = undefined;
    y = undefined;
    constructor(x = 0, y = 0) {
        if (typeof x == "string") {
            x = parseFloat(x);
        }
        if (typeof y == "string") {
            y = parseFloat(y);
        }
        this.x = x;
        this.y = y;
    }
    clone() {
        return new LsPoint(this.x, this.y);
    }
}
export class UserData { // user data
    #data = null; // user manage this data like map (key value pairs)
    constructor() {
        this.#data = new Map();
    }

    get length() {
        return this.data.size;
    }
    get data() {
        return this.#data;
    }
    set data(data) {
        this.#data = data;
    }

    get(key) {
        return this.data.get(key);
    }
    set(key, value) {
        this.data.set(key, value);
    }
    has(key) {
        return this.data.has(key);
    }
    del(key) {
        if (this.data.has(key)) {
            this.data.delete(key);
        }
    }

    clone() {
        let clone = new UserData();
        clone.data = new Map(this.data);
        return clone;
    }
}
export class DataType { // Struct
    static OLLabel = "Label";
    static OLIcon = "Icon";
    static OLPoint = "Point";
    static OLPolyline = "Polyline";
    static OLPolygon = "Polygon";
}
//////////////////////////////////////////////////////////////////
export class OLTextStyle {
    font = '18px Noto Sans KR, sans-serif';
    maxAngle = Math.PI / 4;
    offsetX = 0;
    offsetY = 0;
    overflow = false; // line 경우 true
    placement = "point"; // line
    repeat = undefined;
    scale = undefined;
    rotateWithView = false;
    rotation = 0;
    text = "Text";
    textAlign = "center"; // 'left', 'right', 'center', 'end' or 'start'
    justify = "center"; // 'left', 'center', or 'right'
    textBaseline = "middle"; // 'bottom', 'top', 'middle', 'alphabetic', 'hanging', 'ideographic'
    fill = undefined;
    stroke = undefined;
    backgroundFill = undefined;
    backgroundStroke = undefined;
    padding = [0, 0, 0, 0]; // [top, right, bottom, left]
    declutterMode = undefined; // declutter, obstacle, none
    constructor() {
        this.fill = new OLFillStyle();
        this.backgroundFill = new OLFillStyle();
        this.stroke = new OLStrokeStyle();
        this.backgroundStroke = new OLStrokeStyle();
        this.backgroundFill.color = "#fff";
        this.backgroundStroke.color = "#fff";
    }
    clone() {
        let clone = new OLTextStyle();
        clone.font = this.font;
        clone.maxAngle = this.maxAngle;
        clone.offsetX = this.offsetX;
        clone.offsetY = this.offsetY;
        clone.overflow = this.overflow;
        clone.placement = this.placement;
        clone.repeat = this.repeat;
        clone.scale = this.scale;
        clone.rotateWithView = this.rotateWithView;
        clone.rotation = this.rotation;
        clone.text = this.text;
        clone.textAlign = this.textAlign;
        clone.justify = this.justify;
        clone.textBaseline = this.textBaseline;
        clone.fill = this.fill.clone();
        clone.stroke = this.stroke.clone();
        clone.backgroundFill = this.backgroundFill.clone();
        clone.backgroundStroke = this.backgroundStroke.clone();
        clone.padding = this.padding;
        clone.declutterMode = this.declutterMode;
        return clone;
    }
}
export class OLFillStyle {
    color = '#64646499';
    constructor() {}
    clone() {
        let clone = new OLFillStyle();
        clone.color = this.color;
        return clone;
    }
}
export class OLStrokeStyle {
    color = '#64646499';
    lineCap = "round"; // butt, round, square
    lineJoin = "round"; // bevel, round, miter
    lineDash = null;
    lineDashOffset = 0;
    miterLimit = 10;
    width = 2;
    constructor() {}
    clone() {
        let clone = new OLStrokeStyle();
        clone.color = this.color;
        clone.lineCap = this.lineCap;
        clone.lineJoin = this.lineJoin;
        clone.lineDash = this.lineDash;
        clone.lineDashOffset = this.lineDashOffset;
        clone.miterLimit = this.miterLimit;
        clone.width = this.width;
        return clone;
    }
}
export class OLCircleStyle {
    fill = undefined;
    radius = "25";
    stroke = undefined;
    text = undefined;
    displacement = [0, 0];
    scale = 1;
    rotation = 0;
    rotateWithView = false;
    declutterMode = undefined;
    constructor() {
        this.fill = new OLFillStyle();
        this.stroke = new OLStrokeStyle();
        this.text = new OLTextStyle();
    }
    clone() {
        let clone = new OLCircleStyle();
        clone.fill = this.fill.clone();
        clone.radius = this.radius;
        clone.stroke = this.stroke.clone();
        clone.displacement = this.displacement;
        clone.scale = this.scale;
        clone.rotation = this.rotation;
        clone.rotateWithView = this.rotateWithView;
        clone.declutterMode = this.declutterMode;
        return clone;
    }
}
export class OLIconStyle {
    anchor = [0.5, 0.5];
    anchorOrigin = "top-left"; // bottom-left, bottom-right, top-left or top-right
    anchorXUnits = "fraction"; // pixels
    anchorYUnits = "fraction"; // pixels
    color = undefined;
    crossOrigin = undefined;
    img = undefined; // HTMLImageElement | HTMLCanvasElement | ImageBitmap | undefined
    displacement = [0, 0];
    opacity = 1;
    width = undefined; // The width of the icon in pixels. This can't be used together with scale.
    height = undefined;
    scale = 1;
    rotateWithView = false;
    rotation = 0;
    offset = [0, 0];
    offsetOrigin = "top-left"; // bottom-left, bottom-right, top-left or top-right
    size = undefined;
    src = undefined; // Image source URI
    declutterMode = undefined; // declutter, obstacle, none
    constructor() {}
    clone() {
        let clone = new OLIconStyle();
        clone.anchor = this.anchor;
        clone.anchorOrigin = this.anchorOrigin;
        clone.anchorXUnits = this.anchorXUnits;
        clone.anchorYUnits = this.anchorYUnits;
        clone.color = this.color;
        clone.crossOrigin = this.crossOrigin;
        clone.img = this.img;
        clone.displacement = this.displacement;
        clone.opacity = this.opacity;
        clone.width = this.width;
        clone.height = this.height;
        clone.scale = this.scale;
        clone.rotateWithView = this.rotateWithView;
        clone.rotation = this.rotation;
        clone.offset = this.offset;
        clone.offsetOrigin = this.offsetOrigin;
        clone.size = this.size;
        clone.src = this.src;
        clone.declutterMode = this.declutterMode;
        return clone;
    }
}
export class OLImageStyle {
    opacity = 1;
    rotateWithView = false;
    rotation = 0;
    scale = 1;
    displacement = [0, 0];
    declutterMode = undefined; // declutter, obstacle, none
    constructor() {}
    clone() {
        let clone = new OLImageStyle();
        clone.opacity = this.opacity;
        clone.rotateWithView = this.rotateWithView;
        clone.rotation = this.rotation;
        clone.scale = this.scale;
        clone.displacement = this.displacement;
        clone.declutterMode = this.declutterMode;
        return clone;
    }
}
//////////////////////////////////////////////////////////////////
export class OLData { // Abstract Class LimeS Map Data
    datTp = undefined; // DataType
    ftrTp = undefined; // Shape Geometry Type, Feature Type
    lyrId = undefined; // layer title lid
    uniId = undefined; // int
    userData = undefined; // UserData
    mouse = undefined; // OLMouse
    constructor(datTp, ftrTp, lyrId) {
        if (this.constructor === OLData) {
            throw new Error('Class "OLData" cannot be instantiated')
        }
        this.datTp = datTp;
        this.ftrTp = ftrTp;
        this.lyrId = lyrId;
        this.userData = new UserData();
        this.mouse = new OLMouse();
    }
    clone(clone) {
        throw new Error('Method "clone()" must be implemented.');
    };
    draw(src) {
        throw new Error('Method "draw()" must be implemented.');
    };
    setUniId(uniId) {
        this.uniId = uniId;
    }
    getUniId() {
        return this.uniId;
    }
}
export class OLLabel extends OLData {
    point = undefined; // LsPoint
    style = undefined; // OLTextStyle
    constructor(lyrId) {
        super(DataType.OLLabel, FeatureType.Point, lyrId);
        this.point = new LsPoint();
        this.style = new OLTextStyle();
    }
    get() {
        return this.point;
    }
    set(p) {
        this.point = p.clone();
    }
    clone() {
        let clone = new OLLabel(this.lyrId);
        clone.uniId = this.uniId;
        clone.userData = this.userData.clone();
        clone.point = this.point.clone();
        clone.style = this.style.clone();
        return clone;
    }
    get coordinates() {
        return Object.values(this.point).map(String);
    }
    draw(src, spj, dpj) {
        let geometry = this.coordinates;
        let geo = new PointGeom(geometry).transform(spj, dpj);
        let ftr = new OlFeature({
            geometry: geo,
            properties: this.userData,
        });
        let style = this.mouse.selected === true ? this.getSelectedStyle() : this.getStyle();
        ftr.setStyle(style);
        ftr.setId(this.getUniId());
        src.addFeature(ftr);
    }
    getStyle() {
        let style = new OlStyle({
            text: new TextStyle({
                font: this.style.font,
                maxAngle: this.style.maxAngle,
                offsetX: this.style.offsetX,
                offsetY: this.style.offsetY,
                overflow: this.style.overflow,
                placement: this.style.placement,
                repeat: this.style.repeat,
                scale: this.style.scale,
                rotateWithView: this.style.rotateWithView,
                rotation: this.style.rotation,
                text: this.style.text,
                textAlign: this.style.textAlign,
                justify: this.style.justify,
                textBaseline: this.style.textBaseline,
                fill: new FillStyle({
                    color: this.style.fill.color
                }),
                stroke: new StrokeStyle({
                    color: this.style.stroke.color,
                    lineCap: this.style.stroke.lineCap,
                    lineJoin: this.style.stroke.lineJoin,
                    lineDash: this.style.stroke.lineDash,
                    lineDashOffset: this.style.stroke.lineDashOffset,
                    miterLimit: this.style.stroke.miterLimit,
                    width: this.style.stroke.width,
                }),
                backgroundFill: new FillStyle({
                    color: this.style.backgroundFill.color
                }),
                backgroundStroke: new StrokeStyle({
                    color: this.style.backgroundStroke.color,
                    lineCap: this.style.backgroundStroke.lineCap,
                    lineJoin: this.style.backgroundStroke.lineJoin,
                    lineDash: this.style.backgroundStroke.lineDash,
                    lineDashOffset: this.style.backgroundStroke.lineDashOffset,
                    miterLimit: this.style.backgroundStroke.miterLimit,
                    width: this.style.backgroundStroke.width,
                }),
                padding: this.style.padding,
                declutterMode: this.style.declutterMode,
            }),
        });
        return style;
    }
    getSelectedStyle() {
        let style = new OlStyle({
            text: new TextStyle({
                font: this.style.font,
                maxAngle: this.style.maxAngle,
                offsetX: this.style.offsetX,
                offsetY: this.style.offsetY,
                overflow: this.style.overflow,
                placement: this.style.placement,
                repeat: this.style.repeat,
                scale: this.style.scale,
                rotateWithView: this.style.rotateWithView,
                rotation: this.style.rotation,
                text: this.style.text,
                textAlign: this.style.textAlign,
                justify: this.style.justify,
                textBaseline: this.style.textBaseline,
                fill: new FillStyle({
                    color: "#ff0000"
                }),
                stroke: new StrokeStyle({
                    color: this.style.stroke.color,
                    lineCap: this.style.stroke.lineCap,
                    lineJoin: this.style.stroke.lineJoin,
                    lineDash: this.style.stroke.lineDash,
                    lineDashOffset: this.style.stroke.lineDashOffset,
                    miterLimit: this.style.stroke.miterLimit,
                    width: this.style.stroke.width,
                }),
                backgroundFill: new FillStyle({
                    color: this.style.backgroundFill.color
                }),
                backgroundStroke: new StrokeStyle({
                    color: this.style.backgroundStroke.color,
                    lineCap: this.style.backgroundStroke.lineCap,
                    lineJoin: this.style.backgroundStroke.lineJoin,
                    lineDash: this.style.backgroundStroke.lineDash,
                    lineDashOffset: this.style.backgroundStroke.lineDashOffset,
                    miterLimit: this.style.backgroundStroke.miterLimit,
                    width: this.style.backgroundStroke.width,
                }),
                padding: this.style.padding,
                declutterMode: this.style.declutterMode,
            }),
        });
        return style;
    }
    addPoint(p) {
        this.setPoint(p)
    }
    getPoint() {
        return this.point;
    }
    setPoint(p) {
        this.set(p);
    }
    clearPoint() {
        this.point = new LsPoint();
    }
}
export class OLIcon extends OLData {
    point = undefined; // LsPoint
    style = undefined; // OLIconStyle
    constructor(lyrId) {
        super(DataType.OLIcon, FeatureType.Point, lyrId);
        this.point = new LsPoint();
        this.style = new OLIconStyle();
    }
    get() {
        return this.point;
    }
    set(p) {
        this.point = p.clone();
    }
    clone() {
        let clone = new OLIcon(this.lyrId);
        clone.uniId = this.uniId;
        clone.userData = this.userData.clone();
        clone.point = this.point.clone();
        clone.style = this.style.clone();
        return clone;
    }
    get coordinates() {
        return Object.values(this.point).map(String);
    }
    draw(src, spj, dpj) {
        let geometry = this.coordinates;
        let geo = new PointGeom(geometry).transform(spj, dpj);
        let ftr = new OlFeature({
            geometry: geo,
            properties: this.userData,
        });
        let style = this.mouse.selected === true ? this.getSelectedStyle() : this.getStyle();
        ftr.setStyle(style);
        ftr.setId(this.getUniId());
        src.addFeature(ftr);
    }
    getStyle() {
        let style = new OlStyle({
            image: new IconStyle({
                anchor: this.style.anchor,
                anchorOrigin: this.style.anchorOrigin,
                anchorXUnits: this.style.anchorXUnits,
                anchorYUnits: this.style.anchorYUnits,
                color: this.style.color,
                crossOrigin: this.style.crossOrigin,
                img: this.style.img,
                displacement: this.style.displacement,
                opacity: this.style.opacity,
                width: this.style.width,
                height: this.style.height,
                scale: this.style.scale,
                rotateWithView: this.style.rotateWithView,
                rotation: this.style.rotation,
                offset: this.style.offset,
                offsetOrigin: this.style.offsetOrigin,
                size: this.style.size,
                src: this.style.src,
                declutterMode: this.style.declutterMode,
            }),
        });
        return style;
    }
    getSelectedStyle() {
        let style = new OlStyle({
            image: new IconStyle({
                anchor: this.style.anchor,
                anchorOrigin: this.style.anchorOrigin,
                anchorXUnits: this.style.anchorXUnits,
                anchorYUnits: this.style.anchorYUnits,
                color: this.style.color,
                crossOrigin: this.style.crossOrigin,
                img: this.style.img,
                displacement: this.style.displacement,
                opacity: this.style.opacity,
                //opacity: this.style.opacity * 0.8,
                width: this.style.width,
                height: this.style.height,
                scale: this.style.scale,
                //scale: this.style.scale * 1.5,
                rotateWithView: this.style.rotateWithView,
                rotation: this.style.rotation,
                offset: this.style.offset,
                offsetOrigin: this.style.offsetOrigin,
                size: this.style.size,
                src: this.style.src,
                declutterMode: this.style.declutterMode,
            }),
        });
        return style;
    }
    addPoint(p) {
        this.setPoint(p)
    }
    getPoint() {
        return this.point;
    }
    setPoint(p) {
        this.set(p);
    }
    clearPoint() {
        this.point = new LsPoint();
    }
}
export class OLPoint extends OLData {
    point = undefined; // LsPoint
    style = undefined; // OLCircleStyle
    constructor(lyrId) {
        super(DataType.OLPoint, FeatureType.Point, lyrId);
        this.point = new LsPoint();
        this.style = new OLCircleStyle();
    }
    get() {
        return this.point;
    }
    set(p) {
        this.point = p.clone();
    }
    clone() {
        let clone = new OLPoint(this.lyrId);
        clone.uniId = this.uniId;
        clone.userData = this.userData.clone();
        clone.point = this.point.clone();
        clone.style = this.style.clone();
        return clone;
    }
    get coordinates() {
        return Object.values(this.point).map(String);
    }
    draw(src, spj, dpj) {
        let geometry = this.coordinates;

        /*let objList = [{x: 10, y: 20}, {x: 30, y: 40}, {x: 50, y: 60}];
        let result = objList.map(obj => Object.values(obj).map(String));*/
        /*let arr = [["10", "20"], ["30", "40"], ["50", "60"]];
        let result = arr.map(([x, y]) => ({x, y}));*/

        let geo = new PointGeom(geometry).transform(spj, dpj);
        let ftr = new OlFeature({
            geometry: geo,
            properties: this.userData,
        });
        let style = this.mouse.selected === true ? this.getSelectedStyle() : this.getStyle();
        ftr.setStyle(style);
        ftr.setId(this.getUniId());
        src.addFeature(ftr);
    }
    getStyle() {
        let style = new OlStyle({
            image: new CircleStyle({
                fill: new FillStyle({
                    color: this.style.fill.color,
                }),
                radius: this.style.radius,
                stroke: new StrokeStyle({
                    color: this.style.stroke.color,
                    lineCap: this.style.stroke.lineCap,
                    lineJoin: this.style.stroke.lineJoin,
                    lineDash: this.style.stroke.lineDash,
                    lineDashOffset: this.style.stroke.lineDashOffset,
                    miterLimit: this.style.stroke.miterLimit,
                    width: this.style.stroke.width,
                }),
                displacement: this.style.displacement,
                scale: this.style.scale,
                rotation: this.style.rotation,
                rotateWithView: this.style.rotateWithView,
                declutterMode: this.style.declutterMode,
            }),
            text: new TextStyle({
                text: this.style.text.text,
                font: this.style.text.font,
                fill: new FillStyle({ 
                    color: this.style.text.fill.color 
                }),
                stroke: new StrokeStyle({ 
                    color: this.style.text.stroke.color,
                    width: this.style.text.stroke.width, 
                }),
            }),
        });
        return style;
    }
    getSelectedStyle() {
        let style = new OlStyle({
            image: new CircleStyle({
                fill: new FillStyle({
                    color: this.style.fill.color,
                }),
                radius: this.style.radius,
                stroke: new StrokeStyle({
                    color: this.style.stroke.color,
                    lineCap: this.style.stroke.lineCap,
                    lineJoin: this.style.stroke.lineJoin,
                    lineDash: this.style.stroke.lineDash,
                    lineDashOffset: this.style.stroke.lineDashOffset,
                    miterLimit: this.style.stroke.miterLimit,
                    width: this.style.stroke.width,
                }),
                displacement: this.style.displacement,
                scale: this.style.scale,
                rotation: this.style.rotation,
                rotateWithView: this.style.rotateWithView,
                declutterMode: this.style.declutterMode,
            }),
            text: new TextStyle({
                text: this.style.text.text,
                font: this.style.text.font,
                fill: new FillStyle({ 
                    color: this.style.text.fill.color 
                }),
                stroke: new StrokeStyle({ 
                    color: this.style.text.stroke.color,
                    width: this.style.text.stroke.width, 
                }),
            }),
        });
        return style;
    }
    addPoint(p) {
        this.setPoint(p)
    }
    getPoint() {
        return this.point;
    }
    setPoint(p) {
        this.set(p);
    }
    clearPoint() {
        this.point = new LsPoint();
    }
}
export class OLPolyline extends OLData {
    pointList = undefined; // LsPoint list
    style = undefined; // OLStrokeStyle
    constructor(lyrId) {
        super(DataType.OLPolyline, FeatureType.LineString, lyrId);
        this.pointList = new Array();
        this.style = new OLStrokeStyle();
    }
    get length() {
        return this.pointList.length;
    }
    clone() {
        let clone = new OLPolyline(this.lyrId);
        clone.uniId = this.uniId;
        clone.userData = this.userData.clone();
        for (let i = 0; i < this.length; i++) {
            clone.pointList.push(this.pointList[i].clone());
        }
        clone.style = this.style.clone();
        return clone;
    }
    get coordinates() {
        return this.pointList.map(this.coordinate);
    }
    coordinate(point) {
        return Object.values(point).map(String);
    }
    draw(src, spj, dpj) {
        let geometry = this.coordinates;
        let geo = new LineStringGeom(geometry).transform(spj, dpj);
        let ftr = new OlFeature({
            geometry: geo,
            properties: this.userData,
        });
        let style = this.mouse.selected === true ? this.getSelectedStyle() : this.getStyle();
        ftr.setStyle(style);
        ftr.setId(this.getUniId());
        src.addFeature(ftr);
    }
    getStyle() {
        let style = new OlStyle({
            stroke: new StrokeStyle({
                color: this.style.color,
                lineCap: this.style.lineCap,
                lineJoin: this.style.lineJoin,
                lineDash: this.style.lineDash,
                lineDashOffset: this.style.lineDashOffset,
                miterLimit: this.style.miterLimit,
                width: this.style.width,
            }),
        });
        return style;
    }
    getSelectedStyle() {
        let style = new OlStyle({
            stroke: new StrokeStyle({
                color: "#ff0000",
                lineCap: this.style.lineCap,
                lineJoin: this.style.lineJoin,
                lineDash: this.style.lineDash,
                lineDashOffset: this.style.lineDashOffset,
                miterLimit: this.style.miterLimit,
                width: this.style.width,
            }),
        });
        return style;
    }
    addPoint(p) {
        this.pointList.push(p);
    }
    getPoint(i) {
        if (0 <= i && i < this.length) {
            return this.pointList[i];
        }
        return null;
    }
    setPoint(i, p) {
        if (0 <= i && i < this.length) {
            this.pointList[i] = p.clone();
        }
    }
    clearPoint() {
        this.pointList = new Array();
    }
}
export class OLPolygon extends OLData {
    pointList = undefined; // LsPoint list
    fill = undefined; // OLFillStyle
    stroke = undefined; // OLStrokeStyle
    constructor(lyrId) {
        super(DataType.OLPolygon, FeatureType.Polygon, lyrId);
        this.pointList = new Array();
        this.fill = new OLFillStyle();
        this.stroke = new OLStrokeStyle();
    }
    get length() {
        return this.pointList.length;
    }
    clone() {
        let clone = new OLPolygon(this.lyrId);
        clone.uniId = this.uniId;
        clone.userData = this.userData.clone();
        for (let i = 0; i < this.length; i++) {
            clone.pointList.push(this.pointList[i].clone());
        }
        clone.fill = this.fill.clone();
        clone.stroke = this.stroke.clone();
        return clone;
    }
    get coordinates() {
        return this.pointList.map(this.coordinate);
    }
    coordinate(point) {
        return Object.values(point).map(String);
    }
    draw(src, spj, dpj) {
        let geometry = this.coordinates;
        let geo = new PolygonGeom([geometry]).transform(spj, dpj);
        let ftr = new OlFeature({
            geometry: geo,
            properties: this.userData,
        });
        let style = this.mouse.selected === true ? this.getSelectedStyle() : this.getStyle();
        ftr.setStyle(style);
        ftr.setId(this.getUniId());
        src.addFeature(ftr);
    }
    getStyle() {
        let style = new OlStyle({
            fill: new FillStyle({
                color: this.fill.color,
            }),
            stroke: new StrokeStyle({
                color: this.stroke.color,
                lineCap: this.stroke.lineCap,
                lineJoin: this.stroke.lineJoin,
                lineDash: this.stroke.lineDash,
                lineDashOffset: this.stroke.lineDashOffset,
                miterLimit: this.stroke.miterLimit,
                width: this.stroke.width,
            }),
        });
        return style;
    }
    getSelectedStyle() {
        let style = new OlStyle({
            fill: new FillStyle({
                //color: "#64646499",
                color: this.fill.color,
            }),
            stroke: new StrokeStyle({
                color: this.stroke.color,
                lineCap: this.stroke.lineCap,
                lineJoin: this.stroke.lineJoin,
                lineDash: this.stroke.lineDash,
                lineDashOffset: this.stroke.lineDashOffset,
                miterLimit: this.stroke.miterLimit,
                width: this.stroke.width,
            }),
        });
        return style;
    }
    addPoint(p) {
        this.pointList.push(p);
    }
    getPoint(i) {
        if (0 <= i && i < this.length) {
            return this.pointList[i];
        }
        return null;
    }
    setPoint(i, p) {
        if (0 <= i && i < this.length) {
            this.pointList[i] = p.clone();
        }
    }
    clearPoint() {
        this.pointList = new Array();
    }
}
//////////////////////////////////////////////////////////////////
export class OLOverlay { // Abstract Class LimeS Map Data
    id = undefined; // getOverlayById
    element = undefined; // html
    offset = [0, -10];
    position = undefined; // Coordinate
    positioning = "bottom-center"; // 'bottom-left', 'bottom-center', 'bottom-right', 'center-left', 'center-center', 'center-right', 'top-left', 'top-center', and 'top-right'
    stopEvent = false; // default is true
    insertFirst = true;
    autoPan = false;
    className = "ol-overlay-container ol-selectable";
    constructor(id, element) {
        this.id = id;
        this.element = element;
    }
}
//////////////////////////////////////////////////////////////////
export class OLMouse {
    selected = false; // 0:unknown, 1:selected, 2:moving, 3:resizing
    selectIdx = -1;
    moving = false;
    resizing = false;
    resizeIdx = -1;
    dragging = false;
    dragOffX = 0.0;
    dragOffY = 0.0;
    constructor() {
        this.selected = false; // 0:unknown, 1:selected, 2:moving, 3:resizing
        this.moving = false;
        this.resizing = false;
        this.resizeIdx = -1;
        this.dragging = false;
        this.dragOffX = 0.0;
        this.dragOffY = 0.0;
    }
    default () {
        this.selected = false; // 0:unknown, 1:selected, 2:moving, 3:resizing
        this.selectIdx = -1;
        this.defaultExceptSelected();
    }
    defaultExceptSelected() {
        this.moving = false;
        this.resizing = false;
        this.resizeIdx = -1;
        this.dragging = false;
        this.dragOffX = 0.0;
        this.dragOffY = 0.0;
    }
}
//////////////////////////////////////////////////////////////////
function distanceToPoint(x, y, x1, y1) {
    const dx = x1 - x;
    const dy = y1 - y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance;
}

function distanceToSegment(x, y, x1, y1, x2, y2) {
    // Calculate the length of the line segment
    var segmentLength = Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));

    // Check for a degenerate segment (both endpoints are the same)
    if (segmentLength === 0) {
        return Math.sqrt((x - x1) * (x - x1) + (y - y1) * (y - y1));
    }

    // Calculate the vector from the starting point of the segment to the point
    var sx = x - x1;
    var sy = y - y1;

    // Calculate the normalized direction vector of the segment
    var dx = (x2 - x1) / segmentLength;
    var dy = (y2 - y1) / segmentLength;

    // Calculate the dot product of the direction vector and the vector to the point
    var dotProduct = sx * dx + sy * dy;

    if (dotProduct <= 0) {
        // The nearest point is the starting point of the segment
        return Math.sqrt(sx * sx + sy * sy);
    }

    if (dotProduct >= segmentLength) {
        // The nearest point is the ending point of the segment
        return Math.sqrt((x - x2) * (x - x2) + (y - y2) * (y - y2));
    }

    // The nearest point is on the line segment, so calculate the perpendicular distance
    var distance = Math.abs(sx * dy - sy * dx);
    return distance;
}

function pointInPolygon(pnt, points) {
    let point = [];
    point.push(pnt.x);
    point.push(pnt.y);
    let polygon = [];
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        let pArr = [];
        pArr.push(p.x);
        pArr.push(p.y);
        polygon.push(pArr);
    }
    //A point is in a polygon if a line from the point to infinity crosses the polygon an odd number of times
    let odd = false;
    //For each edge (In this case for each point of the polygon and the previous one)
    for (let i = 0, j = polygon.length - 1; i < polygon.length; i++) {
        //If a line from the point into infinity crosses this edge
        if (((polygon[i][1] > point[1]) !== (polygon[j][1] > point[1])) // One point needs to be above, one below our y coordinate
            // ...and the edge doesn't cross our Y corrdinate before our x coordinate (but between our x coordinate and infinity)
            &&
            (point[0] < ((polygon[j][0] - polygon[i][0]) * (point[1] - polygon[i][1]) / (polygon[j][1] - polygon[i][1]) + polygon[i][0]))) {
            // Invert odd
            odd = !odd;
        }
        j = i;

    }
    //If the number of crossings was odd, the point is in the polygon
    return odd;
};
//////////////////////////////////////////////////////////////////
//http://www.gisdeveloper.co.kr/?p=12587

/*

DESTROY OL MAP

// Assuming you have already created the map
const map = new ol.Map({
  target: 'map',
  // ... other map options
});

// Remove event listeners and interactions
map.un('click', yourClickHandler);
map.getInteractions().forEach(interaction => map.removeInteraction(interaction));
// ... remove other event listeners and interactions

// Destroy the map
map.setTarget(null);

// Remove the map container element from the DOM
const mapContainer = document.getElementById('map');
if (mapContainer) {
  mapContainer.parentNode.removeChild(mapContainer);
}

const url = "http://xdworld.vworld.kr:8080/2d/Base/service/{z}/{x}/{y}.png";
const url = "http://xdworld.vworld.kr:8080/2d/midnight/service/{z}/{x}/{y}.png";
const url = "http://api.vworld.kr/req/wmts/1.0.0/33472E74-6D5A-38C5-9ED1-299F89762FDF/Base/{z}/{y}/{x}.png";
const url = "http://api.vworld.kr/req/wmts/1.0.0/33472E74-6D5A-38C5-9ED1-299F89762FDF/midnight/{z}/{y}/{x}.png";

*/